import * as React from "react";
import { useState, useEffect } from "react";
import { useStyles } from "../../assets/styles/Theme";
import { useTheme } from "@mui/material/styles";
import { AnyForNow } from '../../features/grn/types';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Divider,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
  FormHelperText
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { SETTING_API_17 } from "../../api/ApiModel";
import { externalgetApi } from "../../services/externalApis";
import { useSelector } from "react-redux";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants";
import ViewGrnAttachments from "./ViewGrnAttachments";


import { BUTTON_LABELS, ITEM_LABELS } from "../../utils/sys/GrnLabels";
const GRN_STATUS_ACTION_MAP = {
  4: BUTTON_LABELS.APPROVE,
  5: BUTTON_LABELS.REJECT,
  6: "Return",
  3: BUTTON_LABELS.HOLD,
  1: BUTTON_LABELS.SUBMIT,
  9:"Good Return Submitted",
  10:"Good Return Approved",
  11:"Good Return Rejected",
  12:"Good Return Saved"
};


interface ReviewGrnActionProps {
  statusCode: number;
  grncode: string;
  remark: string;
  setRemark: AnyForNow;
  actionType: string;
  reason: string;
  setReason: AnyForNow;
  reasonCode: string;
  setReasonCode: AnyForNow;
  selectedReasonCatCode: string;
  setSelectedReasonCatCode: AnyForNow;
  onSubmit?: AnyForNow;
  attachments?: AnyForNow[];
  origin: string;
  reasonError?: string;
  setReasonError?: (error: string) => void;
}

const ReviewGrnAction: React.FC<ReviewGrnActionProps> = (props) => {
  const {
    statusCode,
    grncode,
    remark,
    setRemark,
    actionType,
    reason,
    setReason,
    reasonCode,
    setReasonCode,
    selectedReasonCatCode,
    setSelectedReasonCatCode,
    onSubmit,
    attachments = [],
    origin,
    reasonError,
    setReasonError
  } = props;
  const theme = useTheme();
  const styles = useStyles(theme);
  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { STAFFMAPLOCODE, COMPCODE } = SYS_USER.USER_PARAM || {};

  const numericStatusCode = Number(statusCode);

  const status = GRN_STATUS_ACTION_MAP[numericStatusCode] || BUTTON_LABELS.SUBMIT;

  const titleMap = {
    Approve: "Reason for Approve GRN",
    Reject: "Reason for Reject GRN",
    Return: "Reason for Return GRN",
    Hold: "Reason for Hold GRN",
    Submit: "Reason for Submit GRN",
  "Good Return Submitted": "Reason for Submit Good Return GRN",
  "Good Return Approved": "Reason for Approve Good Return GRN",
  "Good Return Rejected": "Reason for Reject Good Return GRN",
  "Good Return Saved": "Reason for Return Good Return GRN",
  };

const reasonCatTitleMap = {
  "1": "Reason for Approve GRN",
  "2": "Reason for Reject GRN",
  "3": "Reason for Return GRN",
  "8": "Reason for Purchases Return Approve",
  "9": "Reason for Purchases Return Reject",
  "6": "Reason for Purchases Return Return",
};


  const title = titleMap[status] || titleMap.Submit;

  const [selectedReason, setSelectedReason] = useState(reason || "");
  const [fileName, setFileName] = useState("No file selected");
  const [reasonOptions, setReasonOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Use local state to track the reason category code for API calls
  // This ensures we can fetch reasons even if parent state hasn't updated yet
  const [localReasonCatCode, setLocalReasonCatCode] = useState(
    selectedReasonCatCode || ""
  );

  // Determine if this screen shows action radio buttons.
  // When radios are shown, we want the user to explicitly choose an action
  // before reasons load or become selectable.
  const hasActionRadios =
    // General GRN actions (Return / Reject / Approve)
    (origin === "CreateGrn" &&
      ![1, 3, 9, 10, 11, 4, 7].includes(numericStatusCode)) ||
    (origin === "EditGrn" &&
      ![1, 3, 9, 10, 11, 4, 7].includes(numericStatusCode)) ||
    (origin === "ViewApprover" &&
      ![3, 9, 10, 11, 6, 4, 8, 12].includes(numericStatusCode)) ||
    (origin === "ViewApprover" && [3].includes(numericStatusCode)) ||
    // Good return flows (also have radios)
    [9, 11, 12].includes(numericStatusCode);

  // Determine default reason category code based on statusCode and origin
  const getDefaultReasonCatCode = (): string => {
    // When radios are visible, don't pre-select any action.
    // Wait for the user to explicitly pick Return / Reject / Approve, etc.
    if (hasActionRadios) return "";

    // Flows without action radios keep their previous default mappings
    if (numericStatusCode === 1) return "5";
    if (numericStatusCode === 3) return "4";
    if (numericStatusCode === 4) return "1";
    if (numericStatusCode === 7) return "4";
    if ([9, 11].includes(numericStatusCode)) return "8";
    if ([12].includes(numericStatusCode)) return "9";
    return "";
  };

  // Initialize local reason category code when component mounts or when prop changes
  useEffect(() => {
    const defaultCode = getDefaultReasonCatCode();
    const codeToUse = selectedReasonCatCode || defaultCode;
    
    if (codeToUse && codeToUse !== localReasonCatCode) {
      setLocalReasonCatCode(codeToUse);
      // Update parent if it's empty
      if (!selectedReasonCatCode && setSelectedReasonCatCode) {
        setSelectedReasonCatCode(codeToUse);
      }
    } else if (!localReasonCatCode && defaultCode) {
      setLocalReasonCatCode(defaultCode);
      if (setSelectedReasonCatCode) {
        setSelectedReasonCatCode(defaultCode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusCode, origin, selectedReasonCatCode]);


const handleActionChange = (event) => {
  const newValue = event.target.value;
  setLocalReasonCatCode(newValue);
  setSelectedReasonCatCode(newValue);
  setSelectedReason(""); 
  setReason(""); 
  setReasonCode(newValue); // <--- this updates reasonCode in parent
};


useEffect(() => {
  // Use localReasonCatCode for API calls to avoid timing issues with parent state
  const codeToFetch = localReasonCatCode || selectedReasonCatCode;
  
  // Ensure reason category code is not empty before making API call
  if (!codeToFetch || codeToFetch === "" || !COMPCODE || !STAFFMAPLOCODE) {
    setReasonOptions([]);
    return;
  }

  setLoading(true);
  console.log("Fetching reasons with params:", {
    COMPCODE,
    LOCCODE: STAFFMAPLOCODE,
    reasoncatcode: codeToFetch,
    STS: CON_STATUS_ACTIVE,
  });
  
  externalgetApi(SETTING_API_17, "get", {
    COMPCODE,
    LOCCODE: STAFFMAPLOCODE,
    reasoncatcode: codeToFetch,
    STS: CON_STATUS_ACTIVE,
  })
    .then((result) => {
      console.log("Reasons fetched successfully:", result);
      // Convert object with numeric keys to array if needed
      let options = [];
      if (Array.isArray(result)) {
        options = result;
      } else if (result && typeof result === 'object') {
        // Convert object with numeric keys to array
        options = Object.values(result).filter(item => item && typeof item === 'object');
      }
      setReasonOptions(options);
    })
    .catch((error) => {
      console.error("Error fetching reasons:", error);
      setReasonOptions([]);
    })
    .finally(() => {
      setLoading(false);
    });
}, [localReasonCatCode, selectedReasonCatCode, COMPCODE, STAFFMAPLOCODE]);

  // Handle dropdown change
const handleReasonChange = (event) => {
  const selectedValue = event.target.value;
  setSelectedReason(selectedValue);

  // Clear the error when a reason is selected
  if (setReasonError && selectedValue) {
    setReasonError("");
  }

  // Find the selected option by description (since value={option.descrip || option})
  const selectedOption = reasonOptions.find(
    (opt) => opt.descrip === selectedValue
  );

  // Set the reason type code in parent (API returns reasontypecode, not reasoncode)
  if (setReasonCode && selectedOption?.reasontypecode) {
    setReasonCode(selectedOption.reasontypecode);
  }

  // Set the reason in parent (description)
  if (setReason) setReason(selectedValue);
};

  // Handle remark change
  const handleRemarkChange = (event) => {
    setRemark(event.target.value);
  };

  // Handle file change
  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      setFileName(event.target.files[0].name);
    } else {
      setFileName("No file selected");
    }
  };

  // Handle Add button click
  const handleAdd = () => {
    if (onSubmit) {
      onSubmit({
        reason: selectedReason,
        descrip: remark,
        fileName,
      });
    }
  };

  // Optionally hide for Approve
  // if (statusCode === 4 || GRN_STATUS_ACTION_MAP[statusCode] === BUTTON_LABELS.APPROVE) {
  //   return null;
  // }
  const labelText =
    // When action radios are visible (Return / Reject / Approve, etc.),
    // drive the label from the selected action (reason category), not from statusCode.
    hasActionRadios
      ? // If no action is selected yet, show a neutral label
        !(localReasonCatCode || selectedReasonCatCode)
        ? "Reason"
        : reasonCatTitleMap[localReasonCatCode || selectedReasonCatCode] ||
          "Reason for Approve GRN"
      : numericStatusCode === 1
      ? "Reason for Submit GRN"
      : numericStatusCode === 3
      ? "Reason for Hold GRN"
      : numericStatusCode === 4
      ? "Reason for Approve GRN"
      : numericStatusCode === 7
      ? "Reason for Hold GRN"
      : reasonCatTitleMap[localReasonCatCode || selectedReasonCatCode] ||
        "Reason for Approve GRN";


  return (
    <>
      <Box pt={2} sx={styles.grn_search_container}>
        <Grid container spacing={6}>
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
{/* Show General GRN actions for all except statusCode 1, 3, 9, 10, 11 */}
{origin === "CreateGrn" && ![1, 3, 9, 10, 11, 4, 7].includes(numericStatusCode) && (
  <RadioGroup
    row
    value={localReasonCatCode || selectedReasonCatCode || ""}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
    <FormControlLabel value="3" control={<Radio />} label="Return" />
    <FormControlLabel value="2" control={<Radio />} label={BUTTON_LABELS.REJECT} />
    <FormControlLabel value="1" control={<Radio />} label={BUTTON_LABELS.APPROVE} />
  </RadioGroup>
)}
{origin === "ViewApprover" && ![3, 9, 10, 11, 6, 4, 8, 12].includes(numericStatusCode) && (
  <RadioGroup
    row
    value={localReasonCatCode || selectedReasonCatCode || ""}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
    <FormControlLabel value="3" control={<Radio />} label="Return" />
    <FormControlLabel value="2" control={<Radio />} label={BUTTON_LABELS.REJECT} />
    <FormControlLabel value="1" control={<Radio />} label={BUTTON_LABELS.APPROVE} />
  </RadioGroup>
)}

{origin === "ViewApprover" && [3].includes(numericStatusCode) && (
  <RadioGroup
    row
    value={localReasonCatCode || selectedReasonCatCode || ""}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
 
    <FormControlLabel value="2" control={<Radio />} label={BUTTON_LABELS.REJECT} />
    <FormControlLabel value="1" control={<Radio />} label={BUTTON_LABELS.APPROVE} />
  </RadioGroup>
)}

{/* {origin === "ViewApprover" && [4].includes(Number(statusCode)) && (
  <RadioGroup
    row
    value={selectedReasonCatCode}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
 
    <FormControlLabel value="2" control={<Radio />} label=BUTTON_LABELS.REJECT />
     <FormControlLabel value="3" control={<Radio />} label="Return" />
  </RadioGroup >
)} */}

{[9, 11].includes(numericStatusCode) && (
  <RadioGroup
    row
    value={localReasonCatCode || selectedReasonCatCode || ""}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
    <FormControlLabel value="8" control={<Radio />} label="Purchases  Return Approve" />
    <FormControlLabel value="9" control={<Radio />} label="Purchases  Return Reject" />
    {/* <FormControlLabel value="6" control={<Radio />} label="Good Return Returned" /> */}
  </RadioGroup>
)}

{/* {[ 10].includes(Number(statusCode)) && (
  <RadioGroup
    row
    value={selectedReasonCatCode}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
  
    <FormControlLabel value="9" control={<Radio />} label="Goods Return Reject" />
    
  </RadioGroup>
)} */}

{[12].includes(numericStatusCode) && (
  <RadioGroup
    row
    value={localReasonCatCode || selectedReasonCatCode || ""}
    onChange={handleActionChange}
    sx={{ mb: 2 }}
  >
  
    <FormControlLabel value="9" control={<Radio />} label="Purchases  Return Reject" />
     <FormControlLabel value="8" control={<Radio />} label="Purchases  Return Approve" />
  </RadioGroup>
)}

            
            
         
            <Divider />

            <Grid container spacing={2} pt={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small" error={Boolean(reasonError)}>
                  <InputLabel>{labelText}</InputLabel>
                  <Select
                    label={labelText}
                    value={selectedReason}
                    onChange={handleReasonChange}
                    error={Boolean(reasonError)}
                    disabled={
                      loading ||
                      // When radios are present, disable until an action is chosen
                      (hasActionRadios &&
                        !(localReasonCatCode || selectedReasonCatCode))
                    }
                  >
                    {reasonOptions.map((option, index) => (
                      <MenuItem key={index} value={option.descrip || option}>
                        {option.descrip || String(option)}
                      </MenuItem>
                    ))}
                  </Select>
                  {reasonError && (
                    <FormHelperText sx={{ fontSize: "12px", color: 'error.main' }}>
                      {reasonError}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={ITEM_LABELS.REMARK}
                  value={remark}
                  onChange={handleRemarkChange}
                  size="small"
                  inputProps={{
                    maxLength: 300,
                  }}
                 helperText={
    remark.length >= 300
      ? "Maximum 300 characters allowed"
      : ""
  }
                  error={remark.length > 300}
                  FormHelperTextProps={{
                    sx: { fontSize: "12px", color: remark.length >= 300 ? "red" : "inherit" },
                  }}
                />
              </Grid>

         {/* <Grid size={{ xs: 12 }}>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "12px",
                      fontWeight: 450,
                    }}
                  >
                    Attachment
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="center"
                    border={1}
                    borderColor="grey.300"
                    borderRadius={1}
                    overflow="hidden"
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AttachFileIcon />}
                      component="label"
                      sx={styles.btn_primary}
                    >
                      Attach
                      <input type="file" hidden onChange={handleFileChange} />
                    </Button>
                    <TextField
                      variant="outlined"
                      disabled
                      fullWidth
                      value={fileName}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "& fieldset": {
                            border: "none",
                          },
                        },
                        "& .MuiInputBase-input.Mui-disabled": {
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Grid>  */}

            </Grid>

            {/* <Box display="flex" justifyContent="flex-end" marginY={2}>
              <Button
                variant="contained"
                color="primary"
                sx={styles.btn_primary}
                onClick={handleAdd}
              >
                Add
              </Button>
            </Box> */}
            <Divider />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default ReviewGrnAction;